import React from "react";
import {
  Laptop2,
  Smartphone,
  Brush,
  Wrench,
  Users,
  Bot,
  ArrowRight,
  Check,
} from "lucide-react";
import axios from "axios";
import { API_URL } from "./Config/Env";
import Swal from "sweetalert2";
import WhatsAppButton from "./Components/WhatsAppButton";

const services = [
  {
    icon: Laptop2,
    title: "Web Development",
    description:
      "Responsive, fast, and scalable websites and web applications tailored to your business goals using modern technologies.",
    price: "From KES 60,000",
    rawPrice: 60000,
    features: [
      "Responsive design",
      "Modern UI/UX",
      "Backend integration",
    ],
  },
  {
    icon: Smartphone,
    title: "Mobile App Development",
    description:
      "High-performance Android and iOS applications designed around real user needs and business workflows.",
    price: "From KES 150,000",
    rawPrice: 150000,
    features: [
      "Android & iOS",
      "API integration",
      "Scalable architecture",
    ],
  },
  {
    icon: Brush,
    title: "Graphic Design",
    description:
      "Professional visual design for brands, products, campaigns, and digital platforms that need a strong visual identity.",
    price: "From KES 5,000",
    rawPrice: 5000,
    features: [
      "Brand graphics",
      "Marketing assets",
      "Digital creatives",
    ],
  },
  {
    icon: Wrench,
    title: "Software Maintenance",
    description:
      "Reliable ongoing support, improvements, updates, performance optimization, and technical maintenance for your systems.",
    price: "From KES 10,000/month",
    rawPrice: 10000,
    features: [
      "Bug fixes",
      "System updates",
      "Ongoing support",
    ],
  },
  {
    icon: Users,
    title: "Technology Consultation",
    description:
      "Practical technical guidance for software strategy, product planning, digital transformation, and solution architecture.",
    price: "From KES 2,000/hour",
    rawPrice: 2000,
    features: [
      "Product strategy",
      "Technical planning",
      "Solution architecture",
    ],
  },
  {
    icon: Bot,
    title: "AI & Automation Solutions",
    description:
      "Automate repetitive work and introduce intelligent systems that help your business operate faster and more efficiently.",
    price: "From KES 5,000",
    rawPrice: 5000,
    features: [
      "Workflow automation",
      "AI integrations",
      "Business process optimization",
    ],
  },
];

export default function Services() {
  const [orderingIndex, setOrderingIndex] = React.useState(null);

  const handleOrderServices = async (
    e,
    index,
    product_name,
    price
  ) => {
    e.preventDefault();

    const quantity = 1;
    const user_id = localStorage.getItem("user_id");

    if (!user_id) {
      Swal.fire({
        icon: "warning",
        title: "Sign In Required",
        text: "Please sign in before requesting this service.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    setOrderingIndex(index);

    try {
      const response = await axios.post(
        `${API_URL}/create_order/`,
        {
          user_id,
          product_name,
          quantity,
          price,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        Swal.fire({
          icon: "success",
          title: "Request Submitted",
          text: "Your service request has been submitted successfully. Our team will contact you shortly.",
          confirmButtonColor: "#2563EB",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Request Failed",
          text:
            response.data.message ||
            "We could not submit your request.",
          confirmButtonColor: "#2563EB",
        });
      }
    } catch (error) {
      console.error("Error ordering service:", error);

      Swal.fire({
        icon: "error",
        title: "Request Failed",
        text:
          "There was an error submitting your request. Please try again.",
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setOrderingIndex(null);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-hidden">
      {/* Hero */}
      <section className="relative bg-slate-950 text-white pt-28 pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[150px]" />

        <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-cyan-400/10 rounded-full blur-[140px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-7">
            <span className="w-2 h-2 bg-blue-400 rounded-full" />
            Our Services
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight mb-7">
            Digital solutions built around
            <span className="block text-blue-400">
              your business goals.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
            From websites and mobile applications to AI automation and
            long-term technical support, we help businesses turn ideas and
            challenges into practical technology.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="relative py-24 px-6 bg-slate-50 overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-blue-100/60 rounded-full blur-[120px]" />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-sm font-semibold mb-5">
              <span className="w-2 h-2 bg-blue-600 rounded-full" />
              What We Build
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-5">
              Technology services designed to
              <span className="text-blue-600"> move your business forward.</span>
            </h2>

            <p className="text-lg text-slate-600 max-w-2xl">
              Choose a service below to get started. Final pricing may vary
              depending on project scope, features, integrations, and delivery
              requirements.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services.map((service, index) => {
              const Icon = service.icon;

              return (
                <div
                  key={index}
                  className="group relative flex flex-col bg-white border border-slate-200 rounded-3xl p-8 overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:border-blue-200 hover:shadow-2xl hover:shadow-blue-900/10"
                >
                  {/* Hover background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-800 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative z-10 flex flex-col h-full">
                    {/* Icon */}
                    <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 mb-7 group-hover:bg-white/15 group-hover:text-white transition-all duration-500">
                      <Icon size={27} />
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-white transition-colors duration-500">
                      {service.title}
                    </h2>

                    {/* Description */}
                    <p className="text-slate-600 leading-relaxed mb-6 group-hover:text-blue-50 transition-colors duration-500">
                      {service.description}
                    </p>

                    {/* Features */}
                    <div className="space-y-3 mb-8">
                      {service.features.map((feature, featureIndex) => (
                        <div
                          key={featureIndex}
                          className="flex items-center gap-3"
                        >
                          <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center group-hover:bg-white/15 transition-colors duration-500">
                            <Check
                              size={14}
                              className="text-blue-600 group-hover:text-white"
                            />
                          </div>

                          <span className="text-sm text-slate-600 group-hover:text-blue-100 transition-colors duration-500">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Bottom */}
                    <div className="mt-auto">
                      <div className="border-t border-slate-200 group-hover:border-white/20 pt-6 mb-6 transition-colors">
                        <p className="text-xs uppercase tracking-wider text-slate-400 group-hover:text-blue-200 mb-2">
                          Starting at
                        </p>

                        <p className="text-xl font-bold text-blue-600 group-hover:text-white transition-colors">
                          {service.price}
                        </p>
                      </div>

                      <button
                        onClick={(e) =>
                          handleOrderServices(
                            e,
                            index,
                            service.title,
                            service.rawPrice
                          )
                        }
                        disabled={orderingIndex === index}
                        className="group/button w-full flex items-center justify-center gap-3 py-3.5 px-5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed group-hover:bg-white group-hover:text-blue-700"
                      >
                        {orderingIndex === index
                          ? "Submitting..."
                          : "Request Service"}

                        {orderingIndex !== index && (
                          <ArrowRight
                            size={17}
                            className="transition-transform duration-300 group-hover/button:translate-x-1"
                          />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Decorative number */}
                  <span className="absolute -bottom-5 -right-1 text-[90px] font-bold text-blue-600/[0.03] group-hover:text-white/[0.05] pointer-events-none">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Custom Solution CTA */}
      <section className="px-6 py-24 bg-white">
        <div className="relative max-w-7xl mx-auto rounded-[2rem] bg-gradient-to-br from-blue-600 via-blue-700 to-slate-950 px-7 py-16 md:px-16 md:py-20 overflow-hidden text-white">
          <div className="absolute -right-20 -top-28 w-[400px] h-[400px] bg-cyan-300/15 rounded-full blur-[120px]" />

          <div className="absolute -left-20 -bottom-32 w-[400px] h-[400px] bg-blue-300/15 rounded-full blur-[120px]" />

          <div className="relative z-10 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
            <div className="max-w-3xl">
              <p className="text-blue-200 text-sm font-semibold uppercase tracking-wider mb-4">
                Need Something Different?
              </p>

              <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-5">
                Have a custom software idea?
              </h2>

              <p className="text-blue-100 text-lg leading-relaxed">
                Tell us what you want to build or the business challenge you
                need solved. We can help plan the solution, define the scope,
                and build the technology around your requirements.
              </p>
            </div>

            <a
              href="/contact"
              className="group inline-flex items-center justify-center gap-3 bg-white text-blue-700 px-7 py-4 rounded-xl font-bold hover:bg-blue-50 transition-all duration-300 hover:-translate-y-1"
            >
              Request a Custom Quote
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
          </div>
        </div>
      </section>

      <WhatsAppButton />
    </div>
  );
}